import { onScopeDispose, ref, watch } from 'vue'

import Network from '@/Network'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'
import { EligibilityResult } from '@/types/qualification-eligibility.types'
import type { EligibilityOutcome } from '@/types/qualification-eligibility.types'
import type { EvaluateEligibilityRequest } from '@/types/qualification-policy.types'

export interface EligibilityPrecheckSource {
  scheduleId: number | null
  stationIds: number[]
  /** `User.id` dos médicos (R6/A8), nunca `Doctor.id`. */
  userIds: number[]
}

/** Tetos do `POST /qualification-eligibility/evaluate` (A8; acima disso o backend responde 400). */
export const EVALUATE_MAX_STATIONS = 200
export const EVALUATE_MAX_USERS = 2000
export const EVALUATE_MAX_PAIRS = 5000

const RESULT_WEIGHT: Record<EligibilityResult, number> = {
  [EligibilityResult.ELIGIBLE]: 0,
  [EligibilityResult.PRE_ALLOCATABLE]: 1,
  [EligibilityResult.NOT_ELIGIBLE]: 2
}

const validIds = (ids: number[]) => [...new Set(ids.filter((id) => Number.isInteger(id) && id > 0))]

const chunk = <T>(list: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, i) =>
    list.slice(i * size, (i + 1) * size)
  )

/** Corpos que cabem nos tetos: plantões em blocos de 200 e médicos em blocos ≤ 5000 / plantões. */
export function buildEvaluateBodies(
  scheduleId: number,
  stationIds: number[],
  userIds: number[]
): EvaluateEligibilityRequest[] {
  return chunk(stationIds, EVALUATE_MAX_STATIONS).flatMap((stations) => {
    const usersPerCall = Math.min(
      EVALUATE_MAX_USERS,
      Math.floor(EVALUATE_MAX_PAIRS / stations.length)
    )
    return chunk(userIds, usersPerCall).map((users) => ({
      scheduleId,
      stationIds: stations,
      userIds: users
    }))
  })
}

/** Pior resultado entre blocos de plantões, com os motivos unidos (mesma regra do backend). */
function mergeOutcome(current: EligibilityOutcome | undefined, next: EligibilityOutcome) {
  if (!current) return next
  const worst = RESULT_WEIGHT[next.result] > RESULT_WEIGHT[current.result] ? next : current
  const reasons = new Map(
    [...current.reasons, ...next.reasons].map((reason) => [`${reason.field}:${reason.key}`, reason])
  )
  return { ...worst, reasons: [...reasons.values()] }
}

/**
 * Chips de elegibilidade na lista de médicos (SPEC §4, pré-checagem). É só UX: a gravação
 * revalida e o 422 continua sendo a fonte da verdade, por isso um erro aqui apenas remove os
 * chips (sem toast) e a alocação segue possível. Chamadas sequenciais e fatiadas nos tetos do
 * backend (R46.5); resposta de uma consulta antiga (outra escala/lista) é descartada.
 */
export function useEligibilityPrecheck(
  source: () => EligibilityPrecheckSource,
  options: { isEnabled: () => boolean; debounceMs?: number }
) {
  const outcomes = ref<Record<number, EligibilityOutcome>>({})
  const isLoading = ref(false)
  // 404 PROFESSIONAL_DOCUMENTS_DISABLED: o backend está sem a feature, não adianta perguntar de novo.
  let isUnavailable = false
  let requestId = 0
  let timer: ReturnType<typeof setTimeout> | undefined

  async function fetchAll(bodies: EvaluateEligibilityRequest[], current: number) {
    const merged: Record<number, EligibilityOutcome> = {}
    for (const body of bodies) {
      // Sequencial de propósito: no máximo uma consulta em voo por modal.
      // eslint-disable-next-line no-await-in-loop
      const result = await Network.qualificationEligibility.evaluate(body)
      if (current !== requestId) return null
      result.forEach((item) => (merged[item.userId] = mergeOutcome(merged[item.userId], item)))
    }
    return merged
  }

  async function run(current: number): Promise<void> {
    const { scheduleId, stationIds, userIds } = source()
    const stations = validIds(stationIds)
    const users = validIds(userIds)
    if (isUnavailable || !scheduleId || !stations.length || !users.length) return
    isLoading.value = true
    try {
      const merged = await fetchAll(buildEvaluateBodies(scheduleId, stations, users), current)
      if (merged) outcomes.value = merged
    } catch (error) {
      if (isProfessionalDocumentsDisabled(error)) isUnavailable = true
      // fallback intencional: sem chips (a lista segue sem indicador); o 422 da gravação ainda vale.
      if (current === requestId) outcomes.value = {}
      console.warn('Falha na pré-checagem de qualificação', error)
    } finally {
      if (current === requestId) isLoading.value = false
    }
  }

  // Escala/plantões mudaram (ou desligou): os chips antigos não valem mais. Só a lista de médicos
  // mudou (busca): os chips de quem continua na lista seguem até a nova resposta.
  let context = ''
  watch(
    () => JSON.stringify({ enabled: options.isEnabled(), ...source() }),
    () => {
      clearTimeout(timer)
      const current = ++requestId
      const { scheduleId, stationIds } = source()
      const nextContext = JSON.stringify([options.isEnabled(), scheduleId, stationIds])
      if (nextContext !== context) outcomes.value = {}
      context = nextContext
      isLoading.value = false
      if (!options.isEnabled()) return
      timer = setTimeout(() => void run(current), options.debounceMs ?? 250)
    },
    { immediate: true }
  )

  onScopeDispose(() => {
    clearTimeout(timer)
    requestId++
  })

  const resultFor = (userId: number): EligibilityResult | null =>
    outcomes.value[userId]?.result ?? null

  return { outcomes, isLoading, resultFor }
}
